import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';
import {
  BulkUserUpdateListResponse,
  BulkUserUpdateDetailResponse,
  TemplateGenerationResponse,
} from './models/bulk-user-update.model';

@Injectable({ providedIn: 'root' })
export class BulkUserUpdateService {
  private _bulkUserUpdatesData$ = new BehaviorSubject<any>(null);
  public readonly bulkUserUpdatesData$ = this._bulkUserUpdatesData$.asObservable();

  private _bulkUserUpdateData$ = new BehaviorSubject<any>(null);
  public readonly bulkUserUpdateData$ = this._bulkUserUpdateData$.asObservable();

  user: any;
  private isGettingBulkUserUpdates = false;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.authService.user$.subscribe((user: any) => {
      if (!user) return;
      this.user = user;
    });
  }

  clearBulkUserUpdatesData = () => {
    this._bulkUserUpdatesData$.next(null);
  }

  clearBulkUserUpdateData = () => {
    this._bulkUserUpdateData$.next(null);
  }

  getBulkUserUpdates = ({
    limit = 10,
    offset = 0,
    status,
    sortColumn,
    sortDirection,
  }: {
    limit?: number;
    offset?: number;
    status?: string;
    sortColumn?: string;
    sortDirection?: string;
  }) => {
    // Prevent multiple calls
    if (this.isGettingBulkUserUpdates) {
      console.log('Already getting bulk user updates, ignoring duplicate call');
      return;
    }

    this.isGettingBulkUserUpdates = true;
    console.log('getBulkUserUpdates called');

    const params: any = {
      loginName: this.user.loginName,
      limit,
      offset,
    };

    if (status) {
      params.status = status;
    }

    if (sortColumn) {
      params.sortColumn = sortColumn;
    }

    if (sortDirection) {
      params.sortDirection = sortDirection;
    }

    return this.http.get(`${environment.expressUrl}/user-update`, {
      headers: { 'Content-Type': 'application/vnd.api+json' },
      params,
    }).subscribe({
      next: (response: BulkUserUpdateListResponse) => {
        console.log('getBulkUserUpdates response received');
        this._bulkUserUpdatesData$.next(response.data.rows);
        this.isGettingBulkUserUpdates = false; // Reset flag
      },
      error: (error) => {
        console.log('error', error);
        this.isGettingBulkUserUpdates = false; // Reset flag on error
      },
    });
  }

  getBulkUserUpdate = (id: string) => {
    return this.http.get(`${environment.expressUrl}/user-update/${id}`, {
      headers: { 'Content-Type': 'application/vnd.api+json' },
      params: { 'loginName': this.user.loginName },
    }).subscribe({
      next: (response: BulkUserUpdateDetailResponse) => {
        this._bulkUserUpdateData$.next(response.data);
      },
      error: (error) => {
        console.log('error', error);
      },
    });
  }

  generateTemplate = ({
    includeInactive = false,
    populateTemplate = true,
    autoOpenTemplate = false,
  }: {
    includeInactive?: boolean;
    populateTemplate?: boolean;
    autoOpenTemplate?: boolean;
  } = {}) => {
    const params: any = {
      loginName: this.user.loginName,
      includeInactive,
      populateTemplate,
      autoOpenTemplate,
    };

    return this.http.get(`${environment.expressUrl}/user-update/generate-template`, {
      headers: { 'Content-Type': 'application/vnd.api+json' },
      params,
    }).subscribe({
      next: (response: TemplateGenerationResponse) => {
        // Download both file types automatically
        if (response.data?.csvFormattedString) {
          this.downloadCsvData(response.data.csvFormattedString, 'bulk-user-update-template.csv');
        }
        if (response.data?.approverReference) {
          this.downloadCsvData(response.data.approverReference, 'approver-reference.csv');
        }
        if (response.data?.assetReference) {
          this.downloadCsvData(response.data.assetReference, 'asset-reference.csv');
        }
      },
      error: (error) => {
        console.log('error', error);
      },
    });
  }

  generateTemplateWithSelection = ({
    includeInactive = false,
    populateTemplate = true,
    autoOpenTemplate = false,
    downloadCsv = true,
    downloadApproverRef = true,
    downloadAssetRef = true,
  }: {
    includeInactive?: boolean;
    populateTemplate?: boolean;
    autoOpenTemplate?: boolean;
    downloadCsv?: boolean;
    downloadApproverRef?: boolean;
    downloadAssetRef?: boolean;
  } = {}) => {
    const params: any = {
      loginName: this.user.loginName,
      includeInactive,
      populateTemplate,
      autoOpenTemplate,
    };

    return this.http.get(`${environment.expressUrl}/user-update/generate-template`, {
      headers: { 'Content-Type': 'application/vnd.api+json' },
      params,
    }).subscribe({
      next: (response: TemplateGenerationResponse) => {
        // Download selected file types
        if (downloadCsv && response.data?.csvFormattedString) {
          this.downloadCsvData(response.data.csvFormattedString, 'bulk-user-update-template.csv');
        }
        if (downloadApproverRef && response.data?.approverReference) {
          this.downloadCsvData(response.data.approverReference, 'approver-reference.csv');
        }
        if (downloadAssetRef && response.data?.assetReference) {
          this.downloadCsvData(response.data.assetReference, 'asset-reference.csv');
        }
      },
      error: (error) => {
        console.log('error', error);
      },
    });
  }

  createBulkUserUpdate = (requestBody: any) => {
    return this.http.post(
      `${environment.expressUrl}/user-update`,
      {
        loginName: this.user.loginName,
        ...requestBody,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  }

  cancelBulkUserUpdate = (id: string) => {
    return this.http.put(
      `${environment.expressUrl}/user-update/${id}`,
      { loginName: this.user.loginName },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
  }

  downloadFile(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadCsvData(csvData: string, filename: string): void {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  downloadJsonData(jsonData: string, filename: string): void {
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  canCancelUpdate(update: any): boolean {
    return update.exeCurrentStatusKid === 'BULK_EVENT_PENDING' ||
      update.exeCurrentStatusKid === 'BULK_EVENT_PROCESSING';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'BULK_EVENT_SUCCESSFUL':
        return 'badge-success';
      case 'BULK_EVENT_FAILED':
        return 'badge-danger';
      case 'BULK_EVENT_CANCELLED':
        return 'badge-warning';
      case 'BULK_EVENT_PROCESSING':
        return 'badge-info';
      case 'BULK_EVENT_PENDING':
        return 'badge-secondary';
      case 'BULK_EVENT_PARTIAL_FAILURE':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }

  getStatusDisplayText(status: string): string {
    switch (status) {
      case 'BULK_EVENT_SUCCESSFUL':
        return 'Successful';
      case 'BULK_EVENT_FAILED':
        return 'Failed';
      case 'BULK_EVENT_CANCELLED':
        return 'Cancelled';
      case 'BULK_EVENT_PROCESSING':
        return 'Processing';
      case 'BULK_EVENT_PENDING':
        return 'Pending';
      case 'BULK_EVENT_PARTIAL_FAILURE':
        return 'Partial Failure';
      default:
        return 'Unknown';
    }
  }
}
